import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ThemeProvider, useTheme, Topbar, LoadingScreen, StatusBadge } from '../shared/DashWidgets';
import Sidebar from '../shared/Sidebar';
import { buildStyles, COLORS } from '../shared/DashTheme';
import api from '../../services/api';

// ── Star Rating ───────────────────────────────────────────────────────────────
const RATING_LABELS = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'];

function StarRating({ value, onChange, readonly = false }) {
  const [hovered, setHovered] = useState(0);
  const active = hovered || value;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ display: 'flex', gap: 4 }}>
        {[1, 2, 3, 4, 5].map(star => (
          <span key={star}
            onClick={() => !readonly && onChange && onChange(star)}
            onMouseEnter={() => !readonly && setHovered(star)}
            onMouseLeave={() => !readonly && setHovered(0)}
            style={{
              fontSize: readonly ? 18 : 26, cursor: readonly ? 'default' : 'pointer',
              transition: 'transform 0.1s',
              transform: !readonly && active >= star ? 'scale(1.25)' : 'scale(1)',
              color: active >= star ? '#f59e0b' : 'rgba(255,255,255,0.25)',
              userSelect: 'none',
            }}>
            {active >= star ? '\u2605' : '\u2606'}
          </span>
        ))}
      </div>
      {!readonly && active > 0 && (
        <span style={{ fontSize: 12, color: '#f59e0b', fontWeight: 600, minWidth: 60 }}>
          {RATING_LABELS[active]}
        </span>
      )}
    </div>
  );
}

// ── Feedback Form ─────────────────────────────────────────────────────────────
function FeedbackForm({ complaintId, onSubmitted, s, existingFeedback }) {
  const isEdit = !!existingFeedback;
  const [rating, setRating] = useState(existingFeedback?.rating || 0);
  const [comment, setComment] = useState(existingFeedback?.comment || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const MAX_CHARS = 300;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rating) { setError('Please select a rating'); return; }
    setLoading(true); setError('');
    try {
      if (isEdit) {
        await api.put(`/community/feedback/${complaintId}`, { rating, comment });
      } else {
        await api.post(`/community/feedback/${complaintId}`, { rating, comment });
      }
      setSubmitted(true);
      setTimeout(() => { onSubmitted(); }, 1800);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit feedback');
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <div style={{ fontSize: 36, marginBottom: 8 }}>🎉</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: '#10b981', marginBottom: 4 }}>
          {isEdit ? 'Feedback Updated!' : 'Thank you for your feedback!'}
        </div>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>Your rating helps improve the system.</div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: 14 }}>
      {error && <div style={{ ...s.alertErr, marginBottom: 10 }}>{error}</div>}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginBottom: 6 }}>Your Rating</div>
        <StarRating value={rating} onChange={setRating} />
      </div>
      <div style={{ position: 'relative', marginBottom: 12 }}>
        <textarea value={comment} onChange={e => setComment(e.target.value.slice(0, MAX_CHARS))}
          placeholder="Optional comment..." rows={3}
          style={{ ...s.textarea, width: '100%', boxSizing: 'border-box', paddingBottom: 22 }} />
        <span style={{ position: 'absolute', bottom: 6, right: 10, fontSize: 10,
          color: comment.length >= MAX_CHARS ? '#ef4444' : 'rgba(255,255,255,0.35)' }}>
          {comment.length}/{MAX_CHARS}
        </span>
      </div>
      <button type="submit" disabled={loading} className="ur-btn-hover"
        style={{ ...s.btnSuccess, opacity: loading ? 0.7 : 1 }}>
        {loading ? (isEdit ? 'Updating...' : 'Submitting...') : (isEdit ? 'Update Feedback' : 'Submit Feedback')}
      </button>
    </form>
  );
}

// ── Feedback List ─────────────────────────────────────────────────────────────
function FeedbackList({ feedbacks, average, count }) {
  if (!feedbacks || feedbacks.length === 0)
    return <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)', fontStyle: 'italic', marginTop: 8 }}>No feedback yet.</div>;

  // Rating distribution
  const dist = [5, 4, 3, 2, 1].map(star => ({
    star,
    count: feedbacks.filter(f => f.rating === star).length,
  }));

  return (
    <div style={{ marginTop: 10 }}>
      {/* Summary row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <StarRating value={Math.round(average)} readonly />
        <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>{average}/5 &middot; {count} review{count !== 1 ? 's' : ''}</span>
      </div>

      {/* Distribution bars */}
      <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {dist.map(({ star, count: c }) => (
          <div key={star} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', width: 14, textAlign: 'right' }}>{star}</span>
            <span style={{ fontSize: 11, color: '#f59e0b' }}>★</span>
            <div style={{ flex: 1, height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: 3,
                width: count > 0 ? `${(c / count) * 100}%` : '0%',
                background: 'linear-gradient(90deg, #f59e0b, #fbbf24)',
                transition: 'width 0.5s ease',
              }} />
            </div>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', width: 14 }}>{c}</span>
          </div>
        ))}
      </div>

      {/* Individual reviews */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {feedbacks.map(fb => (
          <div key={fb.id} style={{ padding: '10px 14px', borderRadius: 9,
            background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{fb.user_name}</span>
                <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20,
                  background: fb.user_role === 'faculty' ? 'rgba(99,102,241,0.3)' : 'rgba(16,185,129,0.3)',
                  color: fb.user_role === 'faculty' ? '#a5b4fc' : '#6ee7b7',
                  textTransform: 'capitalize' }}>{fb.user_role}</span>
              </div>
              <StarRating value={fb.rating} readonly />
            </div>
            {fb.comment && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', fontStyle: 'italic' }}>&ldquo;{fb.comment}&rdquo;</div>}
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 4 }}>{new Date(fb.created_at && !fb.created_at.endsWith('Z') ? fb.created_at + 'Z' : fb.created_at).toLocaleString()}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Timeline ──────────────────────────────────────────────────────────────────
function Timeline({ complaint }) {
  const fmt = d => d ? new Date(!d.endsWith('Z') ? d + 'Z' : d).toLocaleString() : null;
  const events = [
    { label: 'Filed', date: complaint.created_at, color: '#6366f1', icon: '\uD83D\uDCC4' },
    complaint.verified_at && complaint.status !== 'rejected' && { label: `Verified${complaint.verified_by_name ? ' by ' + complaint.verified_by_name : ''}`, date: complaint.verified_at, color: '#10b981', icon: '\u2713' },
    complaint.escalated_at && { label: `Escalated${complaint.escalated_to_name ? ' to ' + complaint.escalated_to_name : ''}`, date: complaint.escalated_at, color: COLORS.amber, icon: '\u26A0', sub: complaint.escalation_reason },
    complaint.resolved_at && { label: `Resolved${complaint.resolved_by_name ? ' by ' + complaint.resolved_by_name : ''}`, date: complaint.resolved_at, color: '#10b981', icon: '\u2714' },
    !complaint.resolved_at && complaint.resolution_deadline && complaint.status !== 'rejected' && { label: 'Resolution Deadline', date: complaint.resolution_deadline, color: complaint.is_overdue ? '#ef4444' : COLORS.amber, icon: '\u23F0' },
    complaint.status === 'rejected' && { label: `Rejected${complaint.rejected_by_name ? ' by ' + complaint.rejected_by_name : ''}`, date: complaint.rejected_at || complaint.updated_at || complaint.created_at, color: '#ef4444', icon: '\u2715' },
    complaint.status === 'rejected' && complaint.rejection_reason && { label: 'Rejection Reason', date: null, color: '#ef4444', icon: '\u2715', sub: complaint.rejection_reason },
  ].filter(Boolean);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {events.map((ev, i) => (
        <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', position: 'relative' }}>
          {i < events.length - 1 && <div style={{ position: 'absolute', left: 11, top: 22, width: 2, height: 'calc(100% + 4px)', background: 'rgba(255,255,255,0.12)' }} />}
          <div style={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0, background: ev.color + '33', border: `2px solid ${ev.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: ev.color, zIndex: 1 }}>{ev.icon}</div>
          <div style={{ paddingBottom: 16 }}>
            <div style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{ev.label}</div>
            {ev.date && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{fmt(ev.date)}</div>}
            {ev.sub && <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', fontStyle: 'italic', marginTop: 4, borderLeft: `2px solid ${ev.color}`, paddingLeft: 8 }}>{ev.sub}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Chat Box ──────────────────────────────────────────────────────────────────
function ChatBox({ complaintId, complaintStatus }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const bottomRef = useRef(null);
  const isClosed = complaintStatus === 'resolved' || complaintStatus === 'rejected';

  const fetchMessages = useCallback(async () => {
    try {
      const res = await api.get(`/complaints/${complaintId}/messages`);
      setMessages(Array.isArray(res.data) ? res.data : []);
    } catch (_) { /* silent */ }
  }, [complaintId]);

  useEffect(() => {
    fetchMessages();
    if (!isClosed) {
      const iv = setInterval(fetchMessages, 5000);
      return () => clearInterval(iv);
    }
  }, [fetchMessages, isClosed]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true); setError('');
    try {
      const res = await api.post(`/complaints/${complaintId}/messages`, { message: text.trim() });
      setMessages(prev => [...prev, res.data]);
      setText('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send');
    } finally { setSending(false); }
  };

  const roleColor = (role) => {
    if (role === 'faculty') return '#a5b4fc';
    if (role === 'admin' || role === 'super_admin') return '#fbbf24';
    if (role === 'authority') return '#f87171';
    return '#6ee7b7';
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Header */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>\uD83D\uDCAC Complaint Chat</div>
        {isClosed
          ? <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: 'rgba(239,68,68,0.2)', color: '#fca5a5' }}>Closed</span>
          : <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: 'rgba(16,185,129,0.2)', color: '#6ee7b7', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', display: 'inline-block' }} />
              Live
            </span>
        }
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px',
        display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0 }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: 12, marginTop: 20, fontStyle: 'italic' }}>
            No messages yet. Start the conversation.
          </div>
        )}
        {messages.map(m => {
          const isMe = m.user_id === user?.id;
          return (
            <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
              {!isMe && (
                <div style={{ fontSize: 10, color: roleColor(m.user_role), fontWeight: 600, marginBottom: 2, paddingLeft: 4 }}>
                  {m.user_name} &middot; <span style={{ textTransform: 'capitalize', opacity: 0.8 }}>{m.user_role}</span>
                </div>
              )}
              <div style={{
                maxWidth: '85%', padding: '8px 12px',
                borderRadius: isMe ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                background: isMe ? 'rgba(99,102,241,0.35)' : 'rgba(255,255,255,0.1)',
                border: `1px solid ${isMe ? 'rgba(99,102,241,0.5)' : 'rgba(255,255,255,0.15)'}`,
                fontSize: 13, color: '#fff', lineHeight: 1.5, wordBreak: 'break-word',
              }}>
                {m.message}
              </div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', marginTop: 2, paddingLeft: 4, paddingRight: 4 }}>
                {new Date(m.created_at && !m.created_at.endsWith('Z') ? m.created_at + 'Z' : m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      {isClosed ? (
        <div style={{ padding: '12px 14px', borderTop: '1px solid rgba(255,255,255,0.1)',
          fontSize: 12, color: 'rgba(255,255,255,0.4)', textAlign: 'center', fontStyle: 'italic' }}>
          Chat closed &mdash; complaint is {complaintStatus}
        </div>
      ) : (
        <form onSubmit={handleSend} style={{ padding: '10px 12px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          {error && <div style={{ fontSize: 11, color: '#fca5a5', marginBottom: 6 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="Type a message..."
              style={{
                flex: 1, padding: '8px 12px', borderRadius: 10, fontSize: 13,
                background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)',
                color: '#fff', outline: 'none',
              }}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(e); } }}
            />
            <button type="submit" disabled={sending || !text.trim()} className="ur-btn-hover"
              style={{
                padding: '8px 14px', borderRadius: 10, fontSize: 16, fontWeight: 600,
                background: COLORS.purple, color: '#fff', border: 'none', cursor: 'pointer',
                opacity: sending || !text.trim() ? 0.5 : 1, transition: 'opacity 0.2s',
              }}>
              {sending ? '...' : '\u2191'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}


// ── Resolution Timer ──────────────────────────────────────────────────────────
function ResolutionTimer({ complaint }) {
  const [now, setNow] = React.useState(Date.now());

  const isActive = ['verified', 'escalated'].includes(complaint.status);
  const isResolved = complaint.status === 'resolved';

  React.useEffect(() => {
    if (!isActive) return;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [isActive]);

  if (!complaint.resolution_deadline || !complaint.verified_at) return null;

  const toUtcMs = d => new Date(d && !d.endsWith('Z') ? d + 'Z' : d).getTime();
  const deadline = toUtcMs(complaint.resolution_deadline);
  const verified = toUtcMs(complaint.verified_at);
  const total = deadline - verified;
  const endTime = isResolved && complaint.resolved_at
    ? toUtcMs(complaint.resolved_at)
    : now;
  const remaining = deadline - endTime;
  const elapsed = endTime - verified;

  const pct = Math.min(100, Math.max(0, (elapsed / total) * 100));
  const isOverdue = remaining < 0;

  const fmt = (ms) => {
    const abs = Math.abs(ms);
    const d = Math.floor(abs / 86400000);
    const h = Math.floor((abs % 86400000) / 3600000);
    const m = Math.floor((abs % 3600000) / 60000);
    const s = Math.floor((abs % 60000) / 1000);
    if (d > 0) return `${d}d ${h}h ${m}m`;
    if (h > 0) return `${h}h ${m}m ${s}s`;
    return `${m}m ${s}s`;
  };

  const barColor = isOverdue ? '#ef4444' : pct > 75 ? '#f59e0b' : '#10b981';
  const label = isResolved
    ? 'Resolved in ' + fmt(elapsed)
    : isOverdue
    ? 'Overdue by ' + fmt(Math.abs(remaining))
    : fmt(remaining) + ' remaining';

  return (
    <div style={{
      borderRadius: 14, padding: '14px 16px',
      background: isOverdue ? 'rgba(239,68,68,0.1)' : isResolved ? 'rgba(16,185,129,0.1)' : 'rgba(99,102,241,0.12)',
      border: `1px solid ${isOverdue ? 'rgba(239,68,68,0.35)' : isResolved ? 'rgba(16,185,129,0.35)' : 'rgba(99,102,241,0.3)'}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 16 }}>{isResolved ? '\u2714' : isOverdue ? '\u26A0' : '\u23F1'}</span>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.6 }}>
          Resolution Timer
        </div>
      </div>

      {/* Countdown */}
      <div style={{ fontSize: 18, fontWeight: 800, color: barColor, marginBottom: 10, fontVariantNumeric: 'tabular-nums' }}>
        {label}
      </div>

      {/* Progress bar */}
      <div style={{ height: 6, borderRadius: 3, background: 'rgba(255,255,255,0.1)', overflow: 'hidden', marginBottom: 10 }}>
        <div style={{
          height: '100%', borderRadius: 3,
          width: `${pct}%`,
          background: barColor,
          transition: 'width 1s linear',
        }} />
      </div>

      {/* Meta */}
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
        <span>Verified {new Date(complaint.verified_at && !complaint.verified_at.endsWith('Z') ? complaint.verified_at + 'Z' : complaint.verified_at).toLocaleDateString()}</span>
        <span>Due {new Date(complaint.resolution_deadline && !complaint.resolution_deadline.endsWith('Z') ? complaint.resolution_deadline + 'Z' : complaint.resolution_deadline).toLocaleDateString()}</span>
      </div>
    </div>
  );
}
// ── Main Component ────────────────────────────────────────────────────────────
function ComplaintDetailInner() {
  const { theme } = useTheme();
  const s = buildStyles(theme);
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();

  const [complaint, setComplaint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  React.useEffect(() => {
    const h = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  const [lightbox, setLightbox] = useState(null);

  const [feedbacks, setFeedbacks] = useState([]);
  const [feedbackAvg, setFeedbackAvg] = useState(null);
  const [feedbackCount, setFeedbackCount] = useState(0);
  const [userFeedback, setUserFeedback] = useState(null);
  const [fbLoading, setFbLoading] = useState(false);
  const [editingFeedback, setEditingFeedback] = useState(false);

  const fetchComplaint = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/complaints/${id}`);
      setComplaint(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load complaint');
    } finally { setLoading(false); }
  }, [id]);

  const fetchFeedbacks = useCallback(async () => {
    try {
      setFbLoading(true);
      const res = await api.get(`/community/feedback/${id}`);
      setFeedbacks(res.data.feedbacks || []);
      setFeedbackAvg(res.data.average);
      setFeedbackCount(res.data.count || 0);
      setUserFeedback(res.data.user_feedback || null);
    } catch (_) { /* not critical */ }
    finally { setFbLoading(false); }
  }, [id]);

  useEffect(() => {
    fetchComplaint();
    fetchFeedbacks();
  }, [fetchComplaint, fetchFeedbacks]);

  const handleFeedbackSubmitted = () => { setEditingFeedback(false); fetchComplaint(); fetchFeedbacks(); };

  const isStudentOrFaculty = user?.role === 'student' || user?.role === 'faculty';
  const isOwner = Number(complaint?.student_id) === Number(user?.id);
  const canGiveFeedback = isStudentOrFaculty && isOwner && complaint?.status === 'resolved' && !userFeedback;

  const metaBox = (label, value) => (
    <div key={label} style={{ padding: '10px 14px', borderRadius: 9, marginBottom: 8,
      background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)' }}>
      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{value}</div>
    </div>
  );

  if (loading) return <LoadingScreen />;

  return (
    <div style={s.page}>
      <div style={s.bgGrid} />
      <div style={s.bgOrb1} />
      <div style={s.bgOrb2} />
      <div style={s.bgOrb3} />
      <div style={s.layout}>
        {sidebarOpen && <Sidebar onToggle={() => setSidebarOpen(o => !o)} mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />}
        <div style={{ ...s.main, marginLeft: isMobile ? 0 : (sidebarOpen ? 248 : 0), transition: 'margin-left 0.3s ease' }}>
          <Topbar
            title="Complaint Details"
            subtitle="Full complaint information"
            onToggleSidebar={() => setSidebarOpen(o => !o)}
            onMobileMenu={() => setMobileOpen(true)}
            actions={
              <button className="ur-btn-hover" style={s.btnSecondary} onClick={() => navigate(-1)}>
                \u2190 Back
              </button>
            }
          />
          <div style={s.content}>
            {error && <div style={s.alertErr}>{error}</div>}
            {!complaint ? (
              !error && <div style={s.alertErr}>Complaint not found</div>
            ) : (
              /* 3-column grid: main content | quick info + chat */
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16, alignItems: 'start' }}>

                {/* ── Left: Main Content ── */}
                <div>
                  {/* Header Card */}
                  <div style={{ ...s.card, marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                      <div style={{ fontSize: 20, fontWeight: 800, color: '#fff', flex: 1, marginRight: 12, lineHeight: 1.3 }}>{complaint.title}</div>
                      <StatusBadge status={complaint.status} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 8, marginBottom: 14 }}>
                      {metaBox('Filed By', complaint.student_name || 'Unknown')}
                      {metaBox('Department', complaint.department || complaint.university_category || 'University-Wide')}
                      {metaBox('Type', complaint.complaint_type === 'university' ? 'University-Wide' : 'Department')}
                      {complaint.university_category && metaBox('Category', complaint.university_category)}
                    </div>
                    <div style={{ ...s.sectionTitle, marginBottom: 8 }}>Description</div>
                    <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', lineHeight: 1.75, whiteSpace: 'pre-wrap' }}>
                      {complaint.description}
                    </div>
                  </div>

                  {/* Deadline Extension Notice */}
                  {complaint.extension_count > 0 && (
                    <div style={{ ...s.card, marginBottom: 16, background: 'rgba(245,158,11,0.12)', border: `1px solid ${COLORS.amber}44` }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.amber, marginBottom: 8 }}>
                        \u23F1 Deadline Extended {complaint.extension_count} time{complaint.extension_count > 1 ? 's' : ''}
                      </div>
                      {complaint.last_extension_reason && (
                        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.8)', fontStyle: 'italic', borderLeft: `3px solid ${COLORS.amber}`, paddingLeft: 12 }}>
                          &ldquo;{complaint.last_extension_reason}&rdquo;
                          {complaint.last_extension_by_name && (
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>
                              &mdash; {complaint.last_extension_by_name}, {new Date(complaint.last_extension_at).toLocaleString()}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Timeline */}
                  <div style={{ ...s.card, marginBottom: 16 }}>
                    <div style={{ ...s.sectionTitle, marginBottom: 16 }}>Timeline</div>
                    <Timeline complaint={complaint} />
                  </div>

                  {/* Attachments */}
                  {complaint.images?.length > 0 && (
                    <div style={{ ...s.card, marginBottom: 16 }}>
                      <div style={{ ...s.sectionTitle, marginBottom: 12 }}>Attachments ({complaint.images.length})</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(100px,1fr))', gap: 10 }}>
                        {complaint.images.map((img, i) => {
                          const url = `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/uploads/complaints/${img}`;
                          const isVideo = /\.(mp4|mov|avi|mkv|webm)$/i.test(img);
                          return isVideo ? (
                            <div key={i} style={{ position: 'relative', cursor: 'pointer' }} onClick={() => setLightbox({ url, isVideo: true })}>
                              <video src={url} muted style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', display: 'block' }} />
                              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)', borderRadius: 8 }}>
                                <span style={{ fontSize: 22 }}>▶</span>
                              </div>
                            </div>
                          ) : (
                            <img key={i} src={url} alt=""
                              onClick={() => setLightbox({ url, isVideo: false })}
                              style={{ width: '100%', height: 80, objectFit: 'cover', borderRadius: 8,
                                cursor: 'pointer', border: '1px solid rgba(255,255,255,0.15)', transition: 'transform 0.15s' }}
                              onMouseOver={e => e.currentTarget.style.transform = 'scale(1.04)'}
                              onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                            />
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Lightbox */}
                  {lightbox && (
                    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      onClick={() => setLightbox(null)}>
                      <button onClick={() => setLightbox(null)} style={{ position: 'absolute', top: 20, right: 28, background: 'none', border: 'none', color: '#fff', fontSize: 32, cursor: 'pointer' }}>✕</button>
                      {lightbox.isVideo
                        ? <video src={lightbox.url} controls autoPlay style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 10 }} onClick={e => e.stopPropagation()} />
                        : <img src={lightbox.url} alt="Full size" style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 10, objectFit: 'contain' }} onClick={e => e.stopPropagation()} />
                      }
                    </div>
                  )}

                  {/* Community Feedback */}
                  <div style={s.card}>
                    <div style={{ ...s.sectionTitle, marginBottom: 4 }}>Community Feedback</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginBottom: 14 }}>
                      {complaint.status === 'resolved'
                        ? 'Students and faculty can rate how this complaint was handled.'
                        : 'Feedback is available once the complaint is resolved.'}
                    </div>
                    {fbLoading ? (
                      <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>Loading feedback...</div>
                    ) : (
                      <>
                        <FeedbackList feedbacks={feedbacks} average={feedbackAvg} count={feedbackCount} />
                        {canGiveFeedback && (
                          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 4 }}>Leave Your Feedback</div>
                            <FeedbackForm complaintId={complaint.id} onSubmitted={handleFeedbackSubmitted} s={s} />
                          </div>
                        )}
                        {userFeedback && !editingFeedback && (
                          <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 9,
                            background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.3)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                              <div style={{ fontSize: 12, color: '#a5b4fc' }}>Your feedback</div>
                              {isOwner && (
                                <button onClick={() => setEditingFeedback(true)} className="ur-btn-hover"
                                  style={{ fontSize: 11, padding: '3px 10px', borderRadius: 6, background: 'rgba(99,102,241,0.3)',
                                    border: '1px solid rgba(99,102,241,0.5)', color: '#a5b4fc', cursor: 'pointer' }}>
                                  ✏ Edit
                                </button>
                              )}
                            </div>
                            <StarRating value={userFeedback.rating} readonly />
                            {userFeedback.comment && (
                              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginTop: 6, fontStyle: 'italic' }}>
                                &ldquo;{userFeedback.comment}&rdquo;
                              </div>
                            )}
                          </div>
                        )}
                        {userFeedback && editingFeedback && (
                          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>Edit Your Feedback</div>
                              <button onClick={() => setEditingFeedback(false)}
                                style={{ fontSize: 11, background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer' }}>
                                Cancel
                              </button>
                            </div>
                            <FeedbackForm complaintId={complaint.id} onSubmitted={handleFeedbackSubmitted} s={s} existingFeedback={userFeedback} />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* ── Right Sidebar ── */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {/* Quick Info */}
                  <div style={s.card}>
                    <div style={{ ...s.sectionTitle, marginBottom: 14 }}>Quick Info</div>
                    {[
                      ['ID', `#${complaint.id}`],
                      ['Status', complaint.status],
                      ['Upvotes', complaint.upvotes || 0],
                      complaint.extension_count > 0 && ['Extensions', complaint.extension_count],
                      complaint.escalation_level > 0 && ['Escalation Level', complaint.escalation_level],
                    ].filter(Boolean).map(([label, value]) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, alignItems: 'center' }}>
                        <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>{label}</span>
                        <span style={{ fontSize: 13, color: '#fff', fontWeight: 600 }}>{value}</span>
                      </div>
                    ))}
                    {complaint.is_overdue && (
                      <div style={{ ...s.alertErr, marginTop: 8, marginBottom: 0, fontSize: 12 }}>\u26A0 Overdue</div>
                    )}
                  </div>

                  {/* Resolution Timer */}
                  {['verified', 'escalated', 'resolved'].includes(complaint.status) && (
                    <ResolutionTimer complaint={complaint} />
                  )}

                  {/* Escalation Info */}
                  {complaint.escalated_at && (
                    <div style={{ ...s.card, background: 'rgba(245,158,11,0.1)', border: `1px solid ${COLORS.amber}44` }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.amber, marginBottom: 10 }}>\u26A0 Escalated</div>
                      {[
                        complaint.escalated_to_name && ['To', complaint.escalated_to_name],
                        ['At', new Date(complaint.escalated_at).toLocaleString()],
                        complaint.escalation_reason && ['Reason', complaint.escalation_reason],
                      ].filter(Boolean).map(([label, value]) => (
                        <div key={label} style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: 0.7 }}>{label}</div>
                          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)' }}>{value}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Rejection Info */}
                  {complaint.status === 'rejected' && (
                    <div style={{ ...s.card, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#ef4444', marginBottom: 10 }}>✕ Rejected</div>
                      {complaint.verified_by_name && (
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: 0.7 }}>By</div>
                          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)' }}>{complaint.verified_by_name}</div>
                        </div>
                      )}
                      {complaint.verified_at && (
                        <div style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: 0.7 }}>At</div>
                          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)' }}>{new Date(complaint.verified_at).toLocaleString()}</div>
                        </div>
                      )}
                      {complaint.rejection_reason && (
                        <div>
                          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: 0.7, marginBottom: 4 }}>Reason</div>
                          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', fontStyle: 'italic',
                            borderLeft: '3px solid rgba(239,68,68,0.6)', paddingLeft: 10, lineHeight: 1.6 }}>
                            "{complaint.rejection_reason}"
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Resolution Info */}
                  {complaint.resolved_at && (
                    <div style={{ ...s.card, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)' }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#10b981', marginBottom: 10 }}>\u2714 Resolved</div>
                      {[
                        complaint.resolved_by_name && ['By', complaint.resolved_by_name],
                        ['At', new Date(complaint.resolved_at).toLocaleString()],
                      ].filter(Boolean).map(([label, value]) => (
                        <div key={label} style={{ marginBottom: 8 }}>
                          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: 0.7 }}>{label}</div>
                          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)' }}>{value}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Chat Box — students and faculty only */}
                  {(user?.role === "student" || user?.role === "faculty") && (
                    <div style={{ ...s.card, padding: 0, overflow: "hidden", minHeight: 420, display: "flex", flexDirection: "column" }}>
                      <ChatBox complaintId={complaint.id} complaintStatus={complaint.status} />
                    </div>
                  )}
                </div>

              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ComplaintDetail() {
  return <ThemeProvider><ComplaintDetailInner /></ThemeProvider>;
}
